import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import type { LoadStatus } from '@apps/stays/types';
import type { CashFundView } from './AccommodationsConsole.telemetry.types';
import type { CashFundBalanceTelemetry } from '@shared/utils/cashFund/cashFund.types';
import type { AccommodationsScenario } from '@shared/types/Accommodations.types';

// Only honeymoon consoles stamp `scenario` here, so every other console's payload is unchanged.
const HONEYMOON_SCENARIOS: ReadonlySet<AccommodationsScenario> = new Set<AccommodationsScenario>(['honeymoon', 'honeymoonAdmin', 'cashFund']);

const honeymoonScenario = (scenario?: AccommodationsScenario) => (scenario && HONEYMOON_SCENARIOS.has(scenario) ? { scenario } : {});

export const accommodationsConsoleTelemetry = createTelemetryObject({
  actions: {
    pageViewed: (scenario?: AccommodationsScenario) => ({
      category: 'wedding',
      action: 'PageViewed',
      extraInfo: {
        name: 'AccommodationsConsole',
        ...honeymoonScenario(scenario),
      },
    }),
    onAccommodationsResultsLoaded: ({
      accommodations_results_loaded_ms,
      load_status,
      cash_fund_view,
      cashFundBalance = {},
      scenario,
    }: {
      accommodations_results_loaded_ms: number;
      load_status: LoadStatus;
      /** Which cash-fund treatment this search actually rendered. `none` on every other console. */
      cash_fund_view: CashFundView;
      /** The couple's spendable balance, so the search can be split by fund size. Empty off the fund console. */
      cashFundBalance?: CashFundBalanceTelemetry;
      /** Which console: reported on honeymoon consoles only. */
      scenario?: AccommodationsScenario;
    }) => ({
      category: 'wedding',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'AccommodationsResultsLoaded',
        accommodations_results_loaded_ms,
        load_status,
        cash_fund_view,
        ...cashFundBalance,
        ...honeymoonScenario(scenario),
      },
    }),
    /**
     * Fired once per search, by the first tile that renders a fund split. Separate from
     * `onAccommodationsResultsLoaded` on purpose: that event fires before the listings carry their
     * split, so it can only report the page variant, never what a tile actually showed.
     */
    cashFundSplitViewed: ({ cash_fund_view }: { cash_fund_view: CashFundView }) => ({
      category: 'accommodations',
      action: 'TravelButtonInteracted',
      extraInfo: {
        name: 'CashFundSplitViewed',
        cash_fund_view,
        from: 'Accommodations Console',
      },
    }),
  },
});

const { TelemetryProvider, useTelemetry } = createTelemetry(accommodationsConsoleTelemetry, { eventId: '', section: '' });

export { TelemetryProvider, useTelemetry as useAccommodationsConsoleTelemetry };
